import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { toast } from 'sonner';
import axios from 'axios';
import { Zap, Flame, Droplets } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const NutsvoorzieningenTab = ({ locatieId, locatieData, onUpdate }) => {
  const [formData, setFormData] = useState({
    stroom: { aansluiting: '', gecontracteerdVermogen: '', verbruikPerMaand: '', beginstand: '' },
    water: { aansluiting: '', verbruikPerMaand: '', beginstand: '' },
    gas: { verbruikPerMaand: '', beginstand: '' },
    bronwater: { aanwezig: false, beginstand: '' },
    zonnepanelen: { aanwezig: false, aantal: 0, vermogen: '' },
    internet: { provider: '', snelheid: '' }
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (locatieData?.nutsvoorzieningen) {
      const nuts = locatieData.nutsvoorzieningen;
      setFormData({
        stroom: {
          aansluiting: nuts.stroom?.aansluiting || '',
          gecontracteerdVermogen: nuts.stroom?.gecontracteerdVermogen || '',
          verbruikPerMaand: nuts.stroom?.verbruikPerMaand || '',
          beginstand: nuts.stroom?.beginstand ?? ''
        },
        water: {
          aansluiting: nuts.water?.aansluiting || '',
          verbruikPerMaand: nuts.water?.verbruikPerMaand || '',
          beginstand: nuts.water?.beginstand ?? ''
        },
        gas: {
          verbruikPerMaand: nuts.gas?.verbruikPerMaand || '',
          beginstand: nuts.gas?.beginstand ?? ''
        },
        bronwater: {
          aanwezig: nuts.bronwater?.aanwezig || false,
          beginstand: nuts.bronwater?.beginstand ?? ''
        },
        zonnepanelen: {
          aanwezig: nuts.zonnepanelen?.aanwezig || false,
          aantal: nuts.zonnepanelen?.aantal || 0,
          vermogen: nuts.zonnepanelen?.vermogen || ''
        },
        internet: {
          provider: nuts.internet?.provider || '',
          snelheid: nuts.internet?.snelheid || ''
        }
      });
    }
  }, [locatieData]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await axios.put(`${API}/locaties/${locatieId}/nutsvoorzieningen`, formData);
      toast.success('Nutsvoorzieningen opgeslagen');
      onUpdate();
    } catch (error) {
      console.error('Error saving nutsvoorzieningen:', error);
      toast.error('Fout bij opslaan');
    } finally {
      setSaving(false);
    }
  };

  const handleNestedChange = (category, field, value) => {
    setFormData(prev => ({
      ...prev,
      [category]: { ...prev[category], [field]: value }
    }));
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2"><Zap className="w-5 h-5 text-amber-600" /> Stroom</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <Label>Aansluiting</Label>
            <Input value={formData.stroom.aansluiting} onChange={(e) => handleNestedChange('stroom', 'aansluiting', e.target.value)} placeholder="3x80A" />
          </div>
          <div>
            <Label>Gecontracteerd Vermogen</Label>
            <Input value={formData.stroom.gecontracteerdVermogen} onChange={(e) => handleNestedChange('stroom', 'gecontracteerdVermogen', e.target.value)} placeholder="55kW" />
          </div>
          <div>
            <Label>Verbruik per maand</Label>
            <Input value={formData.stroom.verbruikPerMaand} onChange={(e) => handleNestedChange('stroom', 'verbruikPerMaand', e.target.value)} placeholder="12.500 kWh" />
          </div>
          <div>
            <Label>Beginmeterstand (kWh)</Label>
            <Input type="number" min="0" value={formData.stroom.beginstand} onChange={(e) => handleNestedChange('stroom', 'beginstand', e.target.value ? Number(e.target.value) : '')} placeholder="bv. 40000" data-testid="stroom-beginstand" />
            <p className="text-xs text-slate-400 mt-1">Startwaarde voor verbruikberekening</p>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2"><Droplets className="w-5 h-5 text-blue-600" /> Leidingwater</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <Label>Aansluiting</Label>
            <Input value={formData.water.aansluiting} onChange={(e) => handleNestedChange('water', 'aansluiting', e.target.value)} placeholder="2 inch" />
          </div>
          <div>
            <Label>Verbruik per maand</Label>
            <Input value={formData.water.verbruikPerMaand} onChange={(e) => handleNestedChange('water', 'verbruikPerMaand', e.target.value)} placeholder="850 m³" />
          </div>
          <div>
            <Label>Beginmeterstand (m³)</Label>
            <Input type="number" min="0" value={formData.water.beginstand} onChange={(e) => handleNestedChange('water', 'beginstand', e.target.value ? Number(e.target.value) : '')} placeholder="bv. 8000" data-testid="water-beginstand" />
            <p className="text-xs text-slate-400 mt-1">Startwaarde voor verbruikberekening</p>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2"><Droplets className="w-5 h-5 text-cyan-600" /> Bronwater</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center space-x-2">
            <Switch id="bronwater-aanwezig" checked={formData.bronwater.aanwezig} onCheckedChange={(checked) => handleNestedChange('bronwater', 'aanwezig', checked)} data-testid="bronwater-aanwezig-switch" />
            <Label htmlFor="bronwater-aanwezig">Bronwater aanwezig</Label>
          </div>
          {formData.bronwater.aanwezig && (
            <div>
              <Label>Beginmeterstand (m³)</Label>
              <Input type="number" min="0" value={formData.bronwater.beginstand} onChange={(e) => handleNestedChange('bronwater', 'beginstand', e.target.value ? Number(e.target.value) : '')} placeholder="bv. 0" data-testid="bronwater-beginstand" />
              <p className="text-xs text-slate-400 mt-1">Startwaarde voor verbruikberekening</p>
            </div>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2"><Flame className="w-5 h-5 text-orange-600" /> Gas</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <Label>Verbruik per maand</Label>
            <Input value={formData.gas.verbruikPerMaand} onChange={(e) => handleNestedChange('gas', 'verbruikPerMaand', e.target.value)} placeholder="Niet van toepassing" />
          </div>
          <div>
            <Label>Beginmeterstand (m³)</Label>
            <Input type="number" min="0" value={formData.gas.beginstand} onChange={(e) => handleNestedChange('gas', 'beginstand', e.target.value ? Number(e.target.value) : '')} placeholder="bv. 1000" data-testid="gas-beginstand" />
            <p className="text-xs text-slate-400 mt-1">Startwaarde voor verbruikberekening</p>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Zonnepanelen</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center space-x-2">
            <Switch id="zonnepanelen" checked={formData.zonnepanelen.aanwezig} onCheckedChange={(checked) => handleNestedChange('zonnepanelen', 'aanwezig', checked)} />
            <Label htmlFor="zonnepanelen">Zonnepanelen aanwezig</Label>
          </div>
          {formData.zonnepanelen.aanwezig && (
            <>
              <div>
                <Label>Aantal panelen</Label>
                <Input type="number" value={formData.zonnepanelen.aantal} onChange={(e) => handleNestedChange('zonnepanelen', 'aantal', parseInt(e.target.value) || 0)} placeholder="48" />
              </div>
              <div>
                <Label>Vermogen</Label>
                <Input value={formData.zonnepanelen.vermogen} onChange={(e) => handleNestedChange('zonnepanelen', 'vermogen', e.target.value)} placeholder="19.2 kWp" />
              </div>
            </>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Internet</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <Label>Provider</Label>
            <Input value={formData.internet.provider} onChange={(e) => handleNestedChange('internet', 'provider', e.target.value)} placeholder="KPN Zakelijk" />
          </div>
          <div>
            <Label>Snelheid</Label>
            <Input value={formData.internet.snelheid} onChange={(e) => handleNestedChange('internet', 'snelheid', e.target.value)} placeholder="500/500 Mbps" />
          </div>
        </CardContent>
      </Card>

      <div className="flex justify-end">
        <Button type="submit" disabled={saving} className="bg-[#1f2548] hover:bg-[#2a3060]">
          {saving ? 'Opslaan...' : 'Opslaan'}
        </Button>
      </div>
    </form>
  );
};

export default NutsvoorzieningenTab;
